import type React from 'react';
import { Pagination, cn } from '@skupreme/shared/ui';
import { CommonConstant } from '@utility/constants/common';

interface ITablePaginationProps {
  /** Kept for call sites written for the table-footer version; unused. */
  colSpan: number;
  currentPage: number;
  descriptionText?: string;
  footerComponent?: React.ReactNode;
  /** Drops the side padding, for a pager that sits flush with its table. */
  noPadding?: boolean;
  pageChanged: (page: number) => void;
  pageSize: number;
  pageSizeChanged: (size: number) => void;
  /** Kept for call sites; the pager derives the page count from the totals. */
  totalPages: number;
  totalData: number;
  /** Kept for call sites; the page-size menu opens upward regardless. */
  upward?: boolean;
}

const PAGE_SIZES = CommonConstant.pageSizes.map(option => Number(option.value));

/**
 * A table's footer: the screen's own `footerComponent` on the left, and the
 * kit pager (page size, range, pages) once there is more than a page of ten.
 */
export const TablePagination: React.FC<ITablePaginationProps> = ({
  currentPage,
  descriptionText = '',
  footerComponent,
  noPadding,
  pageChanged,
  pageSize,
  pageSizeChanged,
  totalData,
}) => {
  const showPager = (currentPage || 0) > 0 && (totalData || 0) > 10;

  if (!footerComponent && !showPager) {
    return null;
  }

  const size = pageSize || PAGE_SIZES[0] || 10;
  const pageSizeOptions = PAGE_SIZES.includes(size)
    ? PAGE_SIZES
    : [...PAGE_SIZES, size].sort((a, b) => a - b);

  return (
    <div
      className={cn(
        'sk:flex sk:flex-wrap sk:items-center sk:justify-between sk:gap-x-4 sk:gap-y-2',
        footerComponent && 'sk:py-2',
        footerComponent && !noPadding && 'sk:px-4',
      )}
    >
      {footerComponent}
      {showPager && (
        <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-wrap sk:items-center sk:justify-end">
          <Pagination
            page={currentPage}
            pageSize={size}
            total={totalData}
            onPageChange={pageChanged}
            onPageSizeChange={pageSizeChanged}
            pageSizeOptions={pageSizeOptions}
            className={cn('sk:flex-1', noPadding && 'sk:px-0')}
          />
          {descriptionText && (
            <span className="sk:px-4 sk:text-xs sk:text-fg-secondary sk:underline">
              {descriptionText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
